import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect } from 'react';

/**
 * Hook to get and manage Auth0 access token
 * Automatically fetches token on mount and provides methods to refresh
 */
export const useAuth0Token = () => {
  const { getAccessTokenSilently, isAuthenticated, logout } = useAuth0();
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchToken = async () => {
    if (!isAuthenticated) {
      setToken(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      // Audience is already configured in auth0Config, so no need to pass it here
      const accessToken = await getAccessTokenSilently();
      setToken(accessToken);
      setError(null);
    } catch (err) {
      console.error('Error fetching access token:', err);
      setError(err as Error);
      setToken(null);

      // If the error is due to missing refresh token or login required, force logout
      const errorMessage = (err as Error).message || '';
      if (errorMessage.includes('Missing Refresh Token') ||
          errorMessage.includes('Login required') ||
          errorMessage.includes('login_required')) {
        console.warn('Auth token error detected, forcing re-authentication');
        logout({
          logoutParams: {
            returnTo: window.location.origin + '/login'
          }
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchToken();
  }, [isAuthenticated]);

  return {
    token,
    isLoading,
    error,
    refreshToken: fetchToken
  };
};
